import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Empty } from 'antd';
import { useAuth } from '../../../../providers/AuthProvider';
import Document from '../../../../models/Document';
import TalentPortal from '../../../talent-portal/TalentPortal';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import DashboardCard from './DashboardCard';

/**
 * Contracts & Payments card for the new talent dashboard.
 * Functional / Tailwind port of ContractsPanel.
 *
 * `contracts` are Document payloads (DocumentTransformer) from the talent stats endpoint:
 * the latest signed contract per opportunity, falling back to the latest unsigned one.
 */

const isSignedContract = contract => (
    contract.type === Document.typeSignedContract() || !!contract.accepted_at
);

const ContractItem = ({ contract }) => {
    const isSigned = isSignedContract(contract);
    const date = contract.accepted_at || contract.uploaded_at;

    return (
        <a
            href={contract.path}
            target="_blank"
            rel="noopener noreferrer"
            title={contract.display_name || contract.name}
            className="flex items-center gap-3 rounded-xl border border-solid border-gray-200 bg-white p-3 text-gray-900 hover:border-primary/30 hover:bg-primary/5 hover:text-gray-900 hover:no-underline"
        >
            <span className="flex h-9 w-8 shrink-0 items-end justify-center rounded-md bg-primary/10 pb-1 text-[9px] font-bold uppercase text-primary" aria-hidden="true">
                {(contract.extension || 'doc').slice(0, 4)}
            </span>

            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold leading-snug">{contract.name}</span>
                <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span
                        className={TB.mapClass('inline-flex items-center rounded-md px-2 py-0.5 text-xxs font-bold uppercase tracking-wider', {
                            'bg-green-50 text-green-700': isSigned,
                            'bg-amber-100 text-amber-700': !isSigned,
                        })}
                    >
                        {isSigned ? 'Signed' : 'Awaiting signature'}
                    </span>
                    {date && TB.moment(date).isValid() && (
                        <span className="text-xs text-gray-500">{TB.moment(date).format('MMM YYYY')}</span>
                    )}
                </span>
            </span>
        </a>
    );
};

const ContractsCard = ({ contracts, totalUpdates, className, title }) => {
    const { auth } = useAuth();

    return (
        <DashboardCard
            title={title || TB.tryTrans('talent.dashboard.contracting_title')}
            count={totalUpdates}
            className={className}
            disabled={!auth?.isProfileApproved?.()}
            disabledReason="Your profile must be complete and your submission accepted in order to view contracts."
            footer={(
                <Link
                    to={TalentPortal.contractsPath()}
                    className="ml-auto inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-primary px-5 py-2.5 text-xs font-semibold text-white hover:bg-primary-dark hover:text-white hover:no-underline"
                >
                    View all contracts
                </Link>
            )}
        >
            {!contracts ? (
                <div className="flex min-h-[160px] items-center justify-center">
                    <LoadingSpinner size="lg" />
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {contracts.length ? (
                        <div className="-mr-1 flex max-h-[325px] flex-col gap-3 overflow-y-auto pr-1">
                            {contracts.map((contract, index) => (
                                <ContractItem key={`contract-${contract.id || index}`} contract={contract} />
                            ))}
                        </div>
                    ) : (
                        <Empty
                            className="m-0 py-6"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={<span className="text-sm text-gray-500">No Recent Contracts Available</span>}
                        />
                    )}
                </div>
            )}
        </DashboardCard>
    );
};

ContractsCard.propTypes = {
    contracts: PropTypes.array,
    totalUpdates: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    className: PropTypes.string,
    title: PropTypes.node,
};

export default ContractsCard;
